import { Link } from 'react-router-dom'

export function ConsentPage() {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-2xl mx-auto px-6 py-10 space-y-8">

        {/* Header */}
        <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">SMS Messaging — Consent &amp; Privacy Notice</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Relay Tracker · Reno Tahoe Odyssey</p>
        </div>

        {/* Program description */}
        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200">About This Service</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
            Relay Tracker is a private web application used by teams competing in the Reno Tahoe Odyssey relay race.
            The app tracks runner positions, leg timing, and van handoffs across the 178-mile course.
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
            As an optional feature, runners may elect to receive automated SMS text message alerts when the
            opposing van begins their final leg of a running set — giving them advance notice to prepare for
            their next set of legs.
          </p>
        </section>

        {/* How opt-in works */}
        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200">How You Opt In</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
            SMS alerts are <strong>off by default</strong>. To receive messages, a runner must:
          </p>
          <ol className="list-decimal list-inside space-y-2 text-sm text-gray-600 dark:text-gray-400 pl-2">
            <li>Log in to the Relay Tracker web app using their assigned runner credentials.</li>
            <li>Navigate to the <strong>Profile</strong> tab.</li>
            <li>Enter their US cell phone number.</li>
            <li>Toggle the <strong>Van Exchange Alert</strong> switch to the ON position.</li>
            <li>Tap <strong>Save Changes</strong>.</li>
          </ol>
          <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
            By completing these steps, the runner explicitly consents to receive automated SMS messages from
            this program on the phone number they provided.
          </p>
        </section>

        {/* Message details */}
        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200">Message Details</h2>
          <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4 text-sm text-gray-700 dark:text-gray-300 space-y-2">
            <p><span className="font-semibold">Message type:</span> Transactional race event alerts</p>
            <p><span className="font-semibold">Frequency:</span> Up to 3 messages per race day (one per van exchange, up to 3 exchanges per race)</p>
            <p><span className="font-semibold">Content:</span> Van handoff time and estimated finish time, e.g.:<br />
              <span className="italic text-gray-500 dark:text-gray-400">"Van 1 started their last leg at 2:14 PM and is expected to finish at 3:02 PM. Get ready for your next set of legs! 🏃"</span>
            </p>
            <p><span className="font-semibold">Sender:</span> Relay Tracker application number</p>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Message &amp; data rates may apply depending on your mobile carrier plan.
          </p>
        </section>

        {/* How to opt out */}
        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200">How to Opt Out</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
            You may opt out at any time by returning to your <strong>Profile</strong> tab in the app and
            toggling the <strong>Van Exchange Alert</strong> switch to OFF, then saving.
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
            You may also reply <strong>STOP</strong> to any message to immediately unsubscribe. Reply
            <strong> HELP</strong> for assistance.
          </p>
        </section>

        {/* Data & privacy */}
        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200">Data &amp; Privacy</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
            Phone numbers are stored in the Relay Tracker database and are used solely for the purpose of
            delivering race-day SMS alerts described above. Phone numbers are not shared with third parties,
            sold, or used for marketing purposes.
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
            SMS delivery is handled by <a href="https://www.twilio.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Twilio</a>, a third-party
            communications platform, in accordance with their{' '}
            <a href="https://www.twilio.com/en-us/legal/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Privacy Policy</a>.
          </p>
        </section>

        {/* Contact */}
        <section className="space-y-2">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200">Contact</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            For questions about this service, contact your team administrator.
          </p>
        </section>

        {/* Back link */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
          <Link to="/login" className="text-sm text-blue-600 hover:underline flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Back to Login
          </Link>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-4">Last updated: March 2026</p>
        </div>

      </div>
    </div>
  )
}
